'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, Clock, Flame, Music2, AlertCircle, Heart, Layers, Star, Search, X, RefreshCw } from 'lucide-react';
import { Act, getActStatus, getStageColor, Stage, DaySchedule } from '@/data/scheduleData';
import DayTabs from './DayTabs';
import StageFilter from './StageFilter';
import ActList from './ActList';
import CountdownBanner from './CountdownBanner';
import { useEvent } from '@/contexts/EventContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import Icon from '@/components/ui/AppIcon';
import { CategoryView } from './CategoryView';
import { CategoryType } from '@/data/categoryData';
import { createClient } from '@/lib/supabase/client';
import { NotificationToggle } from '@/components/FavoriteNotifications';
import { IntroBanner } from './IntroBanner';
import { StageOption } from './StageFilter';


const STORAGE_KEY = 'mps_schedule_v1';

type MainTab = 'spielplan' | 'buehnen' | 'favoriten' | CategoryType;

const CATEGORY_TABS: { key: CategoryType; label: string; icon: string }[] = [
  { key: 'kuenstler', label: 'Künstler', icon: '🎨' },
  { key: 'versorgung', label: 'Versorgung', icon: '🍖' },
  { key: 'handel', label: 'Handel', icon: '🛒' },
  { key: 'handwerk', label: 'Handwerk', icon: '🔨' },
  { key: 'animation', label: 'Animation', icon: '🎭' },
  { key: 'heerlager', label: 'Heerlager', icon: '⚔️' },
];

const CATEGORY_KEYS = new Set<string>(CATEGORY_TABS.map(t => t.key));

const ALL_STAGES: Stage[] = [
  'Musikarena',
  'Folkbühne',
  'Festivalbühne',
  'Kampfplatz',
  'Turnierarena',
  'Kinderbereich',
  'Hinter der Folkbühne',
  'Am Badehaus',
  'Auf dem Gelände',
  'Marktbühne',
  'Bühne der Spielleute',
  'Auf der Waldwiese',
  'Auf der Heerlagerwiese',
  'Walking Act',
  'Künstler',
  'Versorgung',
  'Handel',
  'Handwerk',
  'Animation',
  'Heerlager',
];

const FALLBACK_STAGE_OPTIONS: StageOption[] = ALL_STAGES.map(name => ({ name, icon: '🎭' }));

export default function SchedulePageClient() {
  const { selectedEvent } = useEvent();
  const { favorites } = useFavorites();
  const [activeDay, setActiveDay] = useState<'thu' | 'fri' | 'sat' | 'sun'>('thu');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [now, setNow] = useState<Date>(new Date());
  const [loaded, setLoaded] = useState(false);
  const [mainTab, setMainTab] = useState<MainTab>('spielplan');
  const [searchQuery, setSearchQuery] = useState('');
  const [liveSchedule, setLiveSchedule] = useState<DaySchedule[] | null>(null);
  const [updateToast, setUpdateToast] = useState(false);
  const [dbStages, setDbStages] = useState<StageOption[]>(FALLBACK_STAGE_OPTIONS);

  // Merge live schedule override on top of static data
  const schedule = liveSchedule ?? selectedEvent.schedule;

  // Fetch admin schedule override from Supabase
  const fetchAdminSchedule = useCallback(async (eventId: string) => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('admin_schedule')
        .select('schedule_data')
        .eq('event_id', eventId)
        .maybeSingle();
      if (data?.schedule_data && Array.isArray(data.schedule_data) && data.schedule_data.length > 0) {
        setLiveSchedule(data.schedule_data as DaySchedule[]);
      } else {
        setLiveSchedule(null);
      }
    } catch {
      setLiveSchedule(null);
    }
  }, []);

  // Subscribe to real-time changes on admin_schedule for the selected event
  useEffect(() => {
    const supabase = createClient();
    fetchAdminSchedule(selectedEvent.id);

    const channel = supabase
      .channel(`admin_schedule:${selectedEvent.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_schedule',
          filter: `event_id=eq.${selectedEvent.id}`,
        },
        (payload) => {
          const newData = (payload.new as { schedule_data?: DaySchedule[] })?.schedule_data;
          if (newData && Array.isArray(newData) && newData.length > 0) {
            setLiveSchedule(newData);
          } else {
            setLiveSchedule(null);
          }
          // Show update toast
          setUpdateToast(true);
          setTimeout(() => setUpdateToast(false), 4000);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedEvent.id, fetchAdminSchedule]);

  // Fetch stages from Supabase
  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('mps_stages')
      .select('name, icon, sort_order')
      .eq('event_id', 'global')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setDbStages(data.map((s: { name: string; icon: string }) => ({ name: s.name, icon: s.icon })));
        }
      });
  }, []);

  useEffect(() => {
    setLoaded(true);
    const today = new Date();
    const dayMap: Record<string, 'thu' | 'fri' | 'sat' | 'sun'> = {};
    schedule.forEach(d => {
      dayMap[d.date] = d.dayKey;
    });
    const todayStr = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
    if (dayMap[todayStr]) {
      setActiveDay(dayMap[todayStr]);
    } else {
      setActiveDay(schedule[0]?.dayKey || 'thu');
    }
  }, [selectedEvent.id]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const currentDay = schedule.find(d => d.dayKey === activeDay) || schedule[0];

  const filteredActs = currentDay?.acts.filter(act =>
    stageFilter === 'all' || act.stage === stageFilter
  ) || [];

  const allStages = Array.from(new Set(currentDay?.acts.map(a => a.stage) || []));

  const nextAct = currentDay?.acts
    .filter(act => getActStatus(act, currentDay.date, now) === 'upcoming')
    .sort((a, b) => a.startTime.localeCompare(b.startTime))[0];

  const liveActs = currentDay?.acts.filter(act => getActStatus(act, currentDay.date, now) === 'live') || [];

  // Favorites for this event
  const eventFavorites = favorites.filter(f => f.eventId === selectedEvent.id);
  const favoriteActIds = new Set(eventFavorites.filter(f => f.itemType === 'act').map(f => f.itemId));
  const favoriteActs: Act[] = schedule.flatMap(d => d.acts).filter(a => favoriteActIds.has(a.id));

  // Group favorite acts by day
  const favActsByDay = schedule.map(day => ({
    day,
    acts: day.acts.filter(a => favoriteActIds.has(a.id)),
  })).filter(g => g.acts.length > 0);

  // Search results: search across ALL days of the selected event
  const searchResults: { act: Act; day: typeof schedule[0] }[] = searchQuery.trim().length >= 2
    ? schedule.flatMap(day =>
        day.acts
          .filter(act => {
            const q = searchQuery.toLowerCase();
            return (
              act.name.toLowerCase().includes(q) ||
              act.stage.toLowerCase().includes(q) ||
              act.genre.toLowerCase().includes(q) ||
              act.startTime.includes(q) ||
              act.endTime.includes(q)
            );
          })
          .map(act => ({ act, day }))
      )
    : [];

  if (!loaded) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-muted rounded-md w-64" />
        <div className="h-12 bg-muted rounded-md w-full" />
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`skel-${i + 1}`} className="h-20 bg-muted rounded-md w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 fade-in">
      {/* Live update toast */}
      {updateToast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full shadow-lg text-sm font-medium animate-fade-in">
          <RefreshCw size={14} className="animate-spin" />
          Spielplan aktualisiert
        </div>
      )}

      {/* App intro banner — shown once, dismissible */}
      <IntroBanner />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-primary tracking-wide">
            {selectedEvent.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {selectedEvent.location} · {selectedEvent.dates}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full border border-border">
            <Clock size={12} />
            <span>
              {now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
            </span>
          </div>
        </div>
      </div>

      {/* Main Tabs: Spielplan / Bühnen / Favoriten */}
      <div className="flex gap-1 bg-muted p-1 rounded-xl">
        {([
          { key: 'spielplan', label: 'Spielplan', icon: CalendarDays },
          { key: 'buehnen', label: 'Bühnen', icon: Layers },
          { key: 'favoriten', label: 'Favoriten', icon: Heart },
        ] as { key: MainTab; label: string; icon: any }[]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => {
              setMainTab(key);
              if (key !== 'spielplan') setSearchQuery('');
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-150 ${
              mainTab === key
                ? 'bg-card text-primary shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon size={14} />
            <span>{label}</span>
            {key === 'favoriten' && eventFavorites.length > 0 && (
              <span className="bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {eventFavorites.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Category Tabs: Künstler / Versorgung / Handel / Handwerk / Animation / Heerlager */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 bg-muted/50 p-1 rounded-xl border border-border/50">
        {CATEGORY_TABS.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => { setMainTab(key); setSearchQuery(''); }}
            className={`flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg text-xs font-medium transition-all duration-150 ${
              mainTab === key
                ? 'bg-card text-primary shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
            }`}
          >
            <span className="text-base leading-none">{icon}</span>
            <span className="leading-tight text-center">{label}</span>
          </button>
        ))}
      </div>

      {/* SPIELPLAN TAB */}
      {mainTab === 'spielplan' && (
        <div className="space-y-4">
          {/* Search bar — always visible in Spielplan tab */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={`Acts, Bühnen, Zeiten in ${selectedEvent.name} suchen…`}
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Suche leeren"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Search results — shown when query has 2+ chars */}
          {searchQuery.trim().length >= 2 ? (
            <div className="space-y-2">
              {searchResults.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-3xl mb-2">🔍</p>
                  <p className="font-display text-base font-semibold text-primary">Keine Ergebnisse</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Kein Act gefunden für „{searchQuery}"
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground px-1">
                    {searchResults.length} Ergebnis{searchResults.length !== 1 ? 'se' : ''} in {selectedEvent.name}
                  </p>
                  {searchResults.map(({ act, day }) => {
                    const status = getActStatus(act, day.date, now);
                    const fav = favorites.some(f => f.itemId === act.id && f.eventId === selectedEvent.id);
                    return (
                      <div
                        key={`search-${act.id}`}
                        className={`rounded-xl border border-border overflow-hidden transition-all duration-200 ${
                          status === 'live' ? 'act-row-live border-primary/30' :
                          status === 'finished' ? 'act-row-finished' : 'act-row-upcoming bg-card'
                        }`}
                      >
                        <div className="flex items-center gap-3 px-4 py-3">
                          {/* Day + Time */}
                          <div className="flex-shrink-0 w-16 text-right">
                            <span className="block text-xs font-semibold text-muted-foreground">{day.shortLabel}</span>
                            <span className="font-mono text-sm font-semibold text-foreground tabular-nums">{act.startTime}</span>
                            <span className="block font-mono text-xs text-muted-foreground tabular-nums">{act.endTime}</span>
                          </div>

                          {/* Stage dot */}
                          <div className="flex-shrink-0">
                            <span className={`w-2.5 h-2.5 rounded-full block ${
                              act.stage === 'Musikarena' ? 'bg-primary' :
                              act.stage === 'Marktbühne' ? 'bg-green-700' : 'bg-muted-foreground'
                            }`} />
                          </div>

                          {/* Act info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`font-semibold text-sm ${status === 'finished' ? 'line-through' : 'text-foreground'}`}>
                                {act.name}
                              </span>
                              {act.isHeadliner && <Star size={12} className="text-accent flex-shrink-0" />}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStageColor(act.stage)}`}>
                                {act.stage}
                              </span>
                              <span className="text-xs text-muted-foreground">{act.genre}</span>
                            </div>
                          </div>

                          {/* Status */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {fav && <Heart size={13} className="text-red-500" fill="currentColor" />}
                            {status === 'live' && (
                              <span className="badge-live text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                Live
                              </span>
                            )}
                            {status === 'finished' && (
                              <span className="badge-finished text-xs px-2.5 py-1 rounded-full font-medium">Vorbei</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Live now banner */}
              {liveActs.length > 0 && (
                <div className="rounded-xl overflow-hidden border border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-2 bg-primary px-4 py-2">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    <span className="font-display text-xs font-semibold text-primary-foreground tracking-wider uppercase">
                      Jetzt Live
                    </span>
                  </div>
                  <div className="px-4 py-3 space-y-1.5">
                    {liveActs.map(act => (
                      <div key={`live-${act.id}`} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Music2 size={14} className="text-primary flex-shrink-0" />
                          <span className="font-semibold text-sm text-foreground">{act.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStageColor(act.stage)}`}>
                            {act.stage}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          bis {act.endTime} Uhr
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {nextAct && <CountdownBanner act={nextAct} now={now} dayDate={currentDay.date} />}

              {currentDay?.note && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <AlertCircle size={18} className="text-amber-600 flex-shrink-0" />
                  <p className="text-sm font-medium text-amber-800">{currentDay.note}</p>
                </div>
              )}

              <DayTabs schedule={schedule} activeDay={activeDay} onSelectDay={setActiveDay} now={now} />
              <StageFilter
                stages={dbStages.filter(s => allStages.includes(s.name as Stage))}
                activeStage={stageFilter}
                onSelectStage={setStageFilter}
              />

              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CalendarDays size={12} />
                  Markt ab {currentDay?.openTime} Uhr
                </span>
                {currentDay?.musikarenaOpen && (
                  <span className="flex items-center gap-1.5">
                    <Flame size={12} className="text-primary" />
                    Musikarena ab {currentDay.musikarenaOpen} Uhr
                  </span>
                )}
              </div>

              <div className="medieval-divider" />

              <ActList
                acts={filteredActs}
                dayDate={currentDay?.date || ''}
                now={now}
                eventId={selectedEvent.id}
                emptyMessage={
                  stageFilter !== 'all'
                    ? `Keine Acts auf der ${stageFilter} an diesem Tag.`
                    : 'Keine Acts für diesen Tag eingetragen.'
                }
              />

              <p className="text-xs text-muted-foreground text-center pt-2">
                ⚠️ Inoffizieller Spielplan · Änderungen vorbehalten · Stand: Mai 2026
              </p>
            </>
          )}
        </div>
      )}

      {/* BÜHNEN TAB */}
      {mainTab === 'buehnen' && (
        <div className="space-y-6">
          {ALL_STAGES.map(stage => {
            const stageActs = schedule.flatMap(d => d.acts.filter(a => a.stage === stage));
            if (stageActs.length === 0) return null;
            return (
              <div key={stage} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${getStageColor(stage)}`}>
                    {stage}
                  </span>
                  <span className="text-xs text-muted-foreground">{stageActs.length} Acts</span>
                </div>
                <div className="space-y-1.5">
                  {schedule.map(day => {
                    const dayActs = day.acts.filter(a => a.stage === stage);
                    if (dayActs.length === 0) return null;
                    return (
                      <div key={day.dayKey} className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="px-3 py-1.5 bg-muted border-b border-border">
                          <span className="text-xs font-semibold text-muted-foreground">{day.label} · {day.date}</span>
                        </div>
                        <div className="divide-y divide-border">
                          {dayActs.sort((a, b) => a.startTime.localeCompare(b.startTime)).map(act => {
                            const fav = favorites.some(f => f.itemId === act.id && f.eventId === selectedEvent.id);
                            return (
                              <div key={act.id} className="flex items-center gap-3 px-3 py-2.5">
                                <span className="font-mono text-xs text-muted-foreground w-12 flex-shrink-0">{act.startTime}</span>
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium text-foreground">{act.name}</span>
                                  {act.isHeadliner && <Star size={11} className="inline ml-1 text-accent" />}
                                  <span className="block text-xs text-muted-foreground">{act.genre}</span>
                                </div>
                                {fav && <Heart size={13} className="text-red-500 flex-shrink-0" fill="currentColor" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAVORITEN TAB */}
      {mainTab === 'favoriten' && (
        <div className="space-y-4">
          {eventFavorites.length === 0 ? (
            <div className="text-center py-16">
              <Heart size={48} className="mx-auto text-muted-foreground/30 mb-4" />
              <p className="font-display text-lg font-semibold text-primary">Keine Favoriten</p>
              <p className="text-sm text-muted-foreground mt-1">
                Tippe auf das ♥ Herz-Symbol bei einem Act, um ihn zu speichern.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-muted-foreground">
                  {eventFavorites.length} Favorit{eventFavorites.length !== 1 ? 'en' : ''} für {selectedEvent.name}
                </p>
                <NotificationToggle />
              </div>
              {favActsByDay.map(({ day, acts }) => (
                <div key={day.dayKey} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {day.label} · {day.date}
                    </span>
                  </div>
                  <ActList
                    acts={acts}
                    dayDate={day.date}
                    now={now}
                    eventId={selectedEvent.id}
                    emptyMessage=""
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* CATEGORY TABS */}
      {CATEGORY_KEYS.has(mainTab) && (
        <CategoryView
          category={mainTab as CategoryType}
          eventId={selectedEvent.id}
          isAdmin={false}
        />
      )}
    </div>
  );
}